import { componentHasDefinedNominal } from 'features/assetModeling'
import { shouldSkipBrowserDerivedCdfs } from 'features/dataLayers/ematCategoricalHelpers'
import { createBinnedDataBufferFromData } from 'features/dataLayers/geckoBinnedDataBuffer'
import NominalWorker from 'features/dataLayers/generation/coreLayers/nominal/calculateNominalValues.worker?worker'
import { useBinnedDataBufferCache } from 'features/dataLayers/load/useBinnedDataBufferCache'
import { getInspectionDate } from 'features/fde/inspectionDateHelpers'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import uniq from 'lodash/uniq'
import { useCallback, useMemo } from 'react'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoBinSize,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryType,
} from 'types'
import { formatDateLikeInspectionData } from 'utils/dateHelpers'

import { NominalCalculationProps } from './calculateNominalValues.worker'
import { createNominalCdfEntry } from './createNominalCdfEntry'

const NOMINAL_MEASUREMENT_UNIT = GeckoMeasurementUnit.Millimeters
export type NominalBaseData = {
  nominalDate: Date
  binSize: GeckoBinSize
  cdfEntryTags: GeckoComponentDataFrame['tags']
  generationNumber: number
  referenceCdf?: GeckoComponentDataFrame
}

export function useGenerateNominalCDFs(
  assetModel: GeckoAssetModel | undefined,
  componentId: string | undefined,
  baseData: ReadonlyArray<NominalBaseData> | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined
): ReadonlyArray<GeckoComponentDataFrame> {
  const calculateNominalValuesInWorker = useCallback(
    (props: {
      nominalDate: Date
      outputDataXSize: number
      outputDataYSize: number
      binSize: GeckoBinSize
      geometryXStart: GeckoMeasurement
      geometryYStart: GeckoMeasurement
    }) =>
      Promise.resolve(
        new Promise<{
          nominalDate: Date
          binSizeUsed: GeckoBinSize
          values: Float32Array
        }>((resolve, reject) => {
          const {
            nominalDate,
            outputDataXSize,
            outputDataYSize,
            binSize,
            geometryXStart,
            geometryYStart,
          } = props

          const inspectionDateString = formatDateLikeInspectionData(nominalDate)

          if (
            isNil(assetModel) ||
            isNil(componentId) ||
            !inspectionDateString
          ) {
            reject('Invalid input data, something needed is not defined')
            return
          }

          const nominalCalculationProps: NominalCalculationProps = {
            componentId,
            assetModelJson: assetModel.raw,
            outputUnitId: NOMINAL_MEASUREMENT_UNIT.id,
            xBinCount: outputDataXSize,
            yBinCount: outputDataYSize,
            binHeightJson: binSize.height.toJSON(),
            binWidthJson: binSize.width.toJSON(),
            yGeometryStartJson: geometryYStart.toJSON(),
            xGeometryStartJson: geometryXStart.toJSON(),
            lastDateToApplyNominalsFromStr: inspectionDateString,
            forecastComponentConfig,
          }

          const nominalWorker = new NominalWorker()

          nominalWorker.onmessage = async (message) => {
            nominalWorker.terminate()
            resolve({
              nominalDate: nominalDate,
              binSizeUsed: binSize,
              values: new Float32Array(message.data as number[]),
            })
          }

          nominalWorker.onerror = async (error) => {
            console.error(error)
            nominalWorker.terminate()
            reject(`Couldn't calculate derived CDF, ${error.message}`)
          }

          nominalWorker.postMessage(nominalCalculationProps)
        })
      ),

    [assetModel, componentId, forecastComponentConfig]
  )

  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()

  const assembleNominalCdf = useCallback(
    (
      binSize: GeckoBinSize,
      nominalDate: Date,
      relevantComponentModel: GeckoComponentModel,
      generationNumber: number,
      cdfTags: GeckoComponentDataFrame['tags'],
      referenceCdf?: GeckoComponentDataFrame
    ): GeckoComponentDataFrame => {
      const cdfObject = referenceCdf
        ? createNominalCdfEntry({ referenceCdf, generation: generationNumber })
        : createNominalCdfEntry({
            binSize,
            nominalDate,
            componentModel: relevantComponentModel,
            generation: generationNumber,
            tags: cdfTags,
          })
      const { binning, geometry } = cdfObject.data

      const memoizedGetterFn = memoize(async () =>
        createBinnedDataBufferFromData(
          (
            await calculateNominalValuesInWorker({
              nominalDate,
              outputDataXSize: binning.x_bin_count,
              outputDataYSize: binning.y_bin_count,
              binSize: binning.bin_size,
              geometryXStart: geometry.start.x,
              geometryYStart: geometry.start.y,
            })
          ).values,
          cdfObject
        )
      )

      putBinnedDataBufferInCache({
        id: cdfObject.id,
        generation: generationNumber,
        data: memoizedGetterFn,
      })

      return cdfObject
    },
    [calculateNominalValuesInWorker, putBinnedDataBufferInCache]
  )

  return useMemo(() => {
    const componentModel = assetModel?.data.components.get(componentId ?? '')

    if (
      !componentId ||
      !assetModel ||
      !baseData?.length ||
      !componentModel ||
      !componentHasDefinedNominal(componentModel)
    ) {
      return []
    }

    return baseData.map((curData) =>
      assembleNominalCdf(
        curData.binSize,
        curData.nominalDate,
        componentModel,
        curData.generationNumber,
        curData.cdfEntryTags,
        componentModel.type === GeckoComponentModelType.BasicShipHull
          ? curData.referenceCdf
          : undefined
      )
    )
  }, [assembleNominalCdf, assetModel, componentId, baseData])
}

/**
 * A function that extracts the necessary data to generate a suite of nominal CDFs from the selected entries.
 */
export function getNominalBaseData(
  componentID: string,
  assetModel: GeckoAssetModel,
  componentDatasetsForComponent: ReadonlyArray<GeckoComponentDataset>,
  inspectionsForComponent: ReadonlyArray<GeckoInspection>,
  thicknessCdfsForComponent?: ReadonlyArray<GeckoComponentDataFrame>
): ReadonlyArray<NominalBaseData> {
  const componentModel = assetModel.data.components.get(componentID)
  const defaultNominalBaseData: NominalBaseData = {
    nominalDate: new Date(),
    binSize: GeckoBinSize.OneInchBins,
    cdfEntryTags: {
      [RolodexEntryType.ORGANIZATION]: assetModel.tags.organization,
      [RolodexEntryType.SITE]: assetModel.tags.site,
      [RolodexEntryType.UNIT]: assetModel.tags.site,
      [RolodexEntryType.COMPONENT]: componentID,
    },
    generationNumber: assetModel.generation,
  }

  if (!componentDatasetsForComponent?.length) {
    return [defaultNominalBaseData]
  }

  return componentDatasetsForComponent.flatMap(
    (cds): NominalBaseData | NominalBaseData[] => {
      if (shouldSkipBrowserDerivedCdfs(cds)) {
        return []
      }

      const uniqueBinSizes = uniq(
        cds.data.data_frame_settings
          ? Object.values(cds.data.data_frame_settings).map((dfs) =>
              GeckoBinSize.GetOrCreate(
                dfs.x_axis.bin_size,
                dfs.x_axis.measurement_unit,
                dfs.y_axis.bin_size,
                dfs.y_axis.measurement_unit
              )
            )
          : []
      )

      const curCdsCdfEntryTags = {
        ...defaultNominalBaseData.cdfEntryTags,
        [RolodexEntryType.COMPONENT_DATASET]: cds.id,
        [RolodexEntryType.INSPECTION]: cds.tags.inspection,
        [RolodexEntryType.JOB]: cds.tags.job,
      }
      const curNominalGenerationNumbers = assetModel.generation * cds.generation
      const relevantInspection = inspectionsForComponent.find(
        ({ id }) => id === cds.tags.inspection
      )
      const inspectionDate = getInspectionDate({
        inspectionSlug: relevantInspection?.id,
        inspection: relevantInspection,
      })?.toDate()
      const referenceCdfs =
        componentModel?.type === GeckoComponentModelType.BasicShipHull
          ? thicknessCdfsForComponent?.filter(
              (cdf) =>
                cdf.tags.component === componentID &&
                cdf.tags[RolodexEntryType.COMPONENT_DATASET] === cds.id
            )
          : undefined

      if (referenceCdfs?.length && inspectionDate) {
        return referenceCdfs.map(
          (referenceCdf): NominalBaseData => ({
            nominalDate: inspectionDate,
            binSize: referenceCdf.data.binning.bin_size,
            cdfEntryTags: referenceCdf.tags,
            generationNumber: assetModel.generation * referenceCdf.generation,
            referenceCdf,
          })
        )
      }

      if (!uniqueBinSizes.length) {
        return {
          ...defaultNominalBaseData,
          cdfEntryTags: curCdsCdfEntryTags,
          generationNumber: curNominalGenerationNumbers,
        }
      }

      return uniqueBinSizes.map(
        (binSize): NominalBaseData => ({
          nominalDate: inspectionDate ?? new Date(),
          binSize,
          cdfEntryTags: curCdsCdfEntryTags,
          generationNumber: curNominalGenerationNumbers,
        })
      )
    }
  )
}
